/**
 * ホーム（SPEC §7.1）。起動画面。
 * ヘッダに当月費用（発生主義・推定込み）のサマリを出し、タップでダッシュボードへ。
 */
import { Suspense, lazy, useMemo, useState, type ReactNode } from 'react';
import type { ItemRow, LotRow } from '../../shared/types';
import { AddItemModal } from '../components/AddItemModal';
import { PAGE_BOTTOM } from '../components/layout';
import { FinishLotSheet } from '../components/FinishLotSheet';
import { ItemCard } from '../components/ItemCard';
import { LinkBarcodeSheet } from '../components/LinkBarcodeSheet';
import { PurchaseModal, type PurchaseInput } from '../components/PurchaseModal';
import { useSnackbar } from '../components/Snackbar';
import { SyncBar } from '../components/SyncBar';
import { useAppData, useOnline, useSyncStatus, type ItemView } from '../hooks/useAppData';
import { useForecasts, useMonthly } from '../hooks/useCalc';
import {
  addItem,
  addPurchase,
  deletePurchase,
  finishLot,
  linkBarcode,
  openLot,
  undoFinishLot,
  undoOpenLot,
  type NewItemInput,
} from '../lib/actions';
import { findItemByBarcode } from '../lib/barcode';
import { formatMonth, thisMonth, yen } from '../lib/date';
import { useRouter } from '../lib/router';
import { useSettings } from '../lib/settings';
import { discardOp, retryFailed } from '../lib/sync';

// デコーダ（wasm）を含むので、スキャンボタンを押すまで読み込まない（SPEC §8）
const BarcodeScanner = lazy(() =>
  import('../components/BarcodeScanner').then((m) => ({ default: m.BarcodeScanner })),
);

interface ModalState {
  kind: 'add-item' | 'purchase' | 'finish' | 'scan' | 'link';
  item?: ItemRow;
  lots?: LotRow[];
  barcode?: string;
}

export function Home({ syncing, onRefresh }: { syncing: boolean; onRefresh: () => void }): ReactNode {
  const { loaded, views: sortedViews } = useAppData();
  const { pending, failed } = useSyncStatus();
  const forecasts = useForecasts();
  const { current } = useMonthly();
  const online = useOnline();
  const snack = useSnackbar();
  const { navigate } = useRouter();
  const [settings] = useSettings();
  const [modal, setModal] = useState<ModalState | null>(null);

  // 在庫切れ間近を上部にピン留めするトグル（SPEC §7.1）
  const views = useMemo(() => {
    if (!settings.pinUrgent) return sortedViews;
    return sortedViews
      .slice()
      .sort(
        (a, b) =>
          (forecasts.get(a.item.id)?.daysLeft ?? Infinity) -
          (forecasts.get(b.item.id)?.daysLeft ?? Infinity),
      );
  }, [sortedViews, forecasts, settings.pinUrgent]);

  const handleOpen = async (view: ItemView): Promise<void> => {
    const lot = await openLot(view.item);
    snack({
      message: `${view.item.name} を開封しました`,
      actionLabel: '取り消す',
      onAction: () => undoOpenLot(lot, view.item.name),
    });
  };

  const handleFinish = async (item: ItemRow, lot: LotRow): Promise<void> => {
    const finished = await finishLot(lot, item.name);
    setModal(null);
    snack({
      message: `${item.name} を使い切りました`,
      actionLabel: '取り消す',
      onAction: () => undoFinishLot(finished, item.name),
    });
  };

  const handlePurchase = async (item: ItemRow, input: PurchaseInput): Promise<void> => {
    const purchase = await addPurchase({
      item,
      qty: input.qty,
      totalPrice: input.totalPrice,
      purchasedAt: input.purchasedAt,
      store: input.store,
    });
    setModal(null);
    snack({
      message: `${item.name} の購入を記録しました（${input.totalPrice.toLocaleString()}円）`,
      actionLabel: '取り消す',
      onAction: () => deletePurchase(purchase, item.name),
    });
  };

  const handleAddItem = async (input: NewItemInput): Promise<void> => {
    await addItem(input);
    setModal(null);
  };

  /* --- バーコード（SPEC §11.2） --- */

  /**
   * 既知バーコードなら購入モーダルへ直行、未知なら紐付けシートへ。
   * 判定はキャッシュ済みの品目一覧で行うのでオフラインでも動く。
   */
  const handleDetected = (code: string): void => {
    const item = findItemByBarcode(
      views.map((v) => v.item),
      code,
    );
    if (item) setModal({ kind: 'purchase', item });
    else setModal({ kind: 'link', barcode: code });
  };

  /** 既存品目へ紐付け → そのまま購入入力へ（店頭でスキャンした直後の流れ） */
  const handleLinkExisting = async (item: ItemRow, barcode: string): Promise<void> => {
    try {
      await linkBarcode(item, barcode);
      setModal({ kind: 'purchase', item: { ...item, barcode } });
      snack({ message: `${item.name} にバーコードを紐付けました` });
    } catch (e) {
      setModal(null);
      snack({ message: e instanceof Error ? e.message : String(e), tone: 'error' });
    }
  };

  const handleLinkNew = async (name: string, barcode: string): Promise<void> => {
    const item = await addItem({ name, category: null, defaultDurationDays: 30, barcode });
    setModal({ kind: 'purchase', item });
    snack({ message: `${name} を追加してバーコードを紐付けました` });
  };

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-30 border-b border-ink-700 bg-ink-900/90 backdrop-blur">
        <div className="h-[env(safe-area-inset-top)]" />
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-4">
          <img src="/icons/icon-192.png" alt="" className="size-7 rounded-md" />
          <h1 className="text-base font-black tracking-tight">BURN LOG</h1>
          <div className="ml-auto flex items-center gap-1">
            {/* バーコードスキャン（SPEC §7.1 / §11.2） */}
            <button
              type="button"
              onClick={() => setModal({ kind: 'scan' })}
              aria-label="バーコードをスキャン"
              // 品目キャッシュを読む前にスキャンすると既知コードを未知と誤判定してしまう
              disabled={!loaded}
              className="flex size-10 items-center justify-center rounded-full text-zinc-400
                hover:bg-white/5 hover:text-zinc-200 disabled:opacity-40"
            >
              <svg viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M2 6.5V4a2 2 0 0 1 2-2h2.5M13.5 2H16a2 2 0 0 1 2 2v2.5M18 13.5V16a2 2 0 0 1-2 2h-2.5M6.5 18H4a2 2 0 0 1-2-2v-2.5" strokeLinecap="round" />
                <path d="M5.5 6.5v7M8 6.5v7M10.5 6.5v7M14.5 6.5v7" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onRefresh}
              aria-label="同期"
              className="flex size-10 items-center justify-center rounded-full text-zinc-400
                hover:bg-white/5 hover:text-zinc-200"
            >
              <svg
                viewBox="0 0 20 20"
                className={`size-5 ${syncing ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M16.5 8a6.5 6.5 0 1 0 .3 3.5" />
                <path d="M16.8 3.6V8h-4.4" strokeLinejoin="round" />
              </svg>
            </button>
            <button type="button" className="btn-primary px-3" onClick={() => setModal({ kind: 'add-item' })}>
              ＋ 品目
            </button>
          </div>
        </div>

        {/* 当月費用サマリ → タップで月次ダッシュボードへ（SPEC §7.1） */}
        <button
          type="button"
          onClick={() => navigate({ name: 'dashboard' })}
          className="flex w-full items-center gap-3 border-t border-ink-800 px-4 py-2.5 text-left
            hover:bg-white/[0.03]"
        >
          <span className="text-xs text-zinc-500">{formatMonth(thisMonth())}の費用</span>
          <span className="text-lg font-bold tabular-nums">{yen(current.accrual)}</span>
          {current.accrualEstimated > 0 && (
            <span className="chip bg-ink-800 text-zinc-400">推定込み</span>
          )}
          <svg viewBox="0 0 20 20" className="ml-auto size-4 text-zinc-600" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </header>

      <SyncBar
        online={online}
        pending={pending}
        failed={failed}
        onRetry={() => void retryFailed()}
        onDiscard={(seq) => void discardOp(seq)}
      />

      <main className={`mx-auto max-w-3xl px-3 pt-3 ${PAGE_BOTTOM}`}>
        {!loaded && <p className="px-1 py-10 text-center text-sm text-zinc-500">読み込み中…</p>}

        {loaded && views.length === 0 && (
          <div className="px-4 py-16 text-center">
            <p className="text-sm text-zinc-400">まだ品目がありません。</p>
            <p className="mt-1 text-xs text-zinc-600">
              よく買う消耗品を登録すると、購入・開封・使い切りを 1 タップで記録できます。
            </p>
            <button
              type="button"
              className="btn-primary mt-6 px-5"
              onClick={() => setModal({ kind: 'add-item' })}
            >
              最初の品目を追加
            </button>
          </div>
        )}

        {loaded && views.length > 0 && (
          <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {views.map((view) => (
              <li key={view.item.id}>
                <ItemCard
                  view={view}
                  forecast={forecasts.get(view.item.id)}
                  onOpenDetail={() => navigate({ name: 'item', itemId: view.item.id })}
                  onPurchase={() => setModal({ kind: 'purchase', item: view.item })}
                  onOpen={() => void handleOpen(view)}
                  onFinish={() => {
                    // 1 本だけなら選択シートを出さずに即実行（2タップ以内。SPEC §7.7）
                    if (view.openLots.length === 1) {
                      void handleFinish(view.item, view.openLots[0]);
                    } else {
                      setModal({ kind: 'finish', item: view.item, lots: view.openLots });
                    }
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </main>

      {modal?.kind === 'add-item' && (
        <AddItemModal onClose={() => setModal(null)} onSubmit={(i) => void handleAddItem(i)} />
      )}

      {modal?.kind === 'purchase' && modal.item && (
        <PurchaseModal
          item={modal.item}
          onClose={() => setModal(null)}
          onSubmit={(input) => void handlePurchase(modal.item as ItemRow, input)}
        />
      )}

      {modal?.kind === 'finish' && modal.item && modal.lots && (
        <FinishLotSheet
          item={modal.item}
          lots={modal.lots}
          onClose={() => setModal(null)}
          onPick={(lot) => void handleFinish(modal.item as ItemRow, lot)}
        />
      )}

      {modal?.kind === 'scan' && (
        <Suspense fallback={null}>
          <BarcodeScanner
            title="バーコードをスキャン"
            hint="登録済みの商品なら購入入力へ直行します。"
            onClose={() => setModal(null)}
            onDetected={handleDetected}
          />
        </Suspense>
      )}

      {modal?.kind === 'link' && modal.barcode && (
        <LinkBarcodeSheet
          barcode={modal.barcode}
          items={views.map((v) => v.item)}
          onClose={() => setModal(null)}
          onPick={(item) => void handleLinkExisting(item, modal.barcode as string)}
          onCreate={(name) => void handleLinkNew(name, modal.barcode as string)}
        />
      )}
    </div>
  );
}
